<script lang="ts" generics="T extends object">
  import ScrollShadow from "/@lib/components/table/ScrollShadow.svelte"
  import { createColumnDrag } from "/@lib/components/table/column-drag.svelte"
  import {
    autofitColumn as autofitColumnImpl,
    loadPersisted,
    reconcileColumnOrder,
    type PersistedLayout,
  } from "/@lib/components/table/column-layout"
  import {
    FlexRender,
    createSvelteTable,
  } from "/@lib/components/table/svelte-table"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import {
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    type ColumnDef,
    type ColumnSizingState,
    type ExpandedState,
    type SortingState,
  } from "@tanstack/table-core"
  import { Table } from "@tastyworks/ui-library"
  import { untrack } from "svelte"

  type Props = {
    items: T[]
    columns: ColumnDefinitions
    grouped?: boolean
    withinGroupSort?: (a: T, b: T) => number
    setAllRowsExpanded?: (expanded: boolean) => void
    defaultSort?: { column: string; ascending?: boolean }
    resizable?: boolean
    reorderable?: boolean
    storageKey?: string
  }

  let {
    items,
    columns,
    grouped = true,
    withinGroupSort,
    setAllRowsExpanded = $bindable(() => undefined),
    defaultSort,
    resizable = true,
    reorderable = resizable,
    storageKey,
  }: Props = $props()

  const persisted = untrack(() => loadPersisted(storageKey))
  const initialColumnOrder = untrack(() =>
    reconcileColumnOrder(
      persisted.columnOrder,
      columns.map((c) => c.name)
    )
  )

  const initialSorting: SortingState = untrack(() =>
    persisted.sorting?.length
      ? persisted.sorting
      : defaultSort
        ? [
            {
              id: defaultSort.column,
              desc: !(defaultSort.ascending ?? true),
            },
          ]
        : []
  )

  const initialColumnSizing: ColumnSizingState = persisted.columnSizing ?? {}
  const initialExpanded: ExpandedState = {}

  const GROUP_BRAND: unique symbol = Symbol("group-row")
  type GroupRow = {
    [GROUP_BRAND]: true
    groupKey: string
    subRows: T[]
  }
  type Row = T | GroupRow

  function isGroupRow(row: Row): row is GroupRow {
    return (
      typeof row === "object" &&
      row !== null &&
      (row as GroupRow)[GROUP_BRAND] === true
    )
  }

  const data = $derived.by<Row[]>(() => {
    if (!grouped) return items as Row[]
    const groupByFormatter = columns[0].dataFormatter
    const groups: Record<string, T[]> = {}
    for (const item of items) {
      const key = groupByFormatter(item) as string
      groups[key] = groups[key] || []
      groups[key].push(item)
    }
    if (withinGroupSort) {
      for (const key of Object.keys(groups)) groups[key].sort(withinGroupSort)
    }
    return Object.entries(groups).map(([groupKey, subRows]): GroupRow => ({
      [GROUP_BRAND]: true,
      groupKey,
      subRows,
    }))
  })

  const tableColumns = $derived.by<ColumnDef<Row>[]>(() => {
    return columns.map((col) => ({
      id: col.name,
      header: col.name,
      enableSorting: col.sortable !== false,
      enableResizing: resizable,
      // table-core auto-detects sortDescFirst from the first row's accessor
      // value; group rows return undefined which it reads as numeric and
      // flips to desc-first, breaking the asc → desc → none cycle.
      sortDescFirst: false,
      // accessorFn is required for column.getCanSort() to return true.
      accessorFn: (row) => {
        if (isGroupRow(row)) return undefined
        const cmp = col.comparator ?? col.dataFormatter
        return cmp(row as T)
      },
      cell: ({ row }) => {
        const orig = row.original
        if (isGroupRow(orig)) return ""
        const value = col.dataFormatter(orig as T)
        return value ?? ""
      },
    }))
  })

  const table = createSvelteTable<Row>({
    get data() {
      return data
    },
    get columns() {
      return tableColumns
    },
    initialState: {
      columnSizing: initialColumnSizing,
      columnOrder: initialColumnOrder,
      sorting: initialSorting,
      expanded: initialExpanded,
    },
    onStateChange: () => {
      if (!storageKey) return
      // Defer past the in-flight state update so the snapshot reads the
      // post-update state, not the pre.
      queueMicrotask(() => {
        const s = table.getState()
        const snapshot: PersistedLayout = {
          columnSizing: { ...(s.columnSizing ?? {}) },
          columnOrder: [...(s.columnOrder ?? [])],
          sorting: [...(s.sorting ?? [])],
        }
        try {
          localStorage.setItem(storageKey, JSON.stringify(snapshot))
        } catch {
          /* quota / private mode */
        }
      })
    },
    enableColumnResizing: resizable,
    columnResizeMode: "onChange",
    getSubRows: (row) => (isGroupRow(row) ? row.subRows : undefined),
    getRowId: (row, index, parent) => {
      if (isGroupRow(row)) return `g:${row.groupKey}`
      return parent ? `${parent.id}:${index}` : `r:${index}`
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  setAllRowsExpanded = (expand: boolean) => table.toggleAllRowsExpanded(expand)

  // Svelte 5 doesn't track deps through opaque table-core calls like
  // `getRowModel()` — touching the state slices here makes the deriveds
  // re-run when those slices change.
  const headerGroups = $derived.by(() => {
    void table.state.sorting
    void table.state.columnSizing
    void table.state.columnOrder
    return table.getHeaderGroups()
  })

  // table-core caches `getRowModel().rows` and the row instances themselves
  // across columnOrder updates, and `row.getVisibleCells()` inside the
  // template is opaque to Svelte's reactivity. Materializing both layers
  // in a derived gives Svelte fresh array references to diff against, so
  // the body reorders alongside the header.
  const rowsWithCells = $derived.by(() => {
    void table.state.sorting
    void table.state.expanded
    void table.state.columnSizing
    void table.state.columnOrder
    return table.getRowModel().rows.map((row) => ({
      row,
      cells: row.getVisibleCells(),
    }))
  })

  const drag = createColumnDrag({
    getColumnOrder: () => table.state.columnOrder ?? [],
    setColumnOrder: (order) => table.setColumnOrder(order),
  })

  function autofitColumn(name: string, e: Event) {
    const root = (e.target as HTMLElement).closest(
      "[role='table']"
    ) as HTMLElement | null
    if (!root) return
    const width = autofitColumnImpl({
      rootElement: root,
      visibleColumns: table.getVisibleLeafColumns(),
      columns,
      items,
      columnId: name,
      // Grouped sub-rows get a 3rem left indent (see body cell markup).
      groupedFirstColumnIndentRem: grouped ? 3 : 0,
    })
    if (width === null) return
    table.setColumnSizing((prev) => ({ ...prev, [name]: width }))
  }

  const DEFAULT_COLUMN_WIDTH = 150

  function widthStyle(name: string): string {
    const w = table.state.columnSizing?.[name] ?? DEFAULT_COLUMN_WIDTH
    return `width: ${w}px; flex: 0 0 auto`
  }

  // Sum of column widths — used by the group row's full-span cell so it
  // matches the aggregate width of data rows.
  const totalColumnWidth = $derived.by(() => {
    const sizing = table.state.columnSizing ?? {}
    let total = 0
    for (const col of table.getVisibleLeafColumns()) {
      total += sizing[col.id] ?? DEFAULT_COLUMN_WIDTH
    }
    return total
  })
</script>

<ScrollShadow class="h-full">
  <Table.Root class="flex h-full flex-col overflow-x-auto border-2">
    <div
      role="rowgroup"
      class="header text-general-default-text sticky top-0 z-[1] flex w-fit min-w-full leading-4 [&>*:first-child]:pl-2"
    >
      {#each headerGroups as headerGroup}
        {#each headerGroup.headers as header}
          {@const col = header.column}
          {@const name = col.id}
          {@const sortable = col.getCanSort()}
          {@const sortEntry = (table.state.sorting ?? []).find(
            (s) => s.id === name
          )}
          {@const sortDir = sortEntry
            ? sortEntry.desc
              ? "desc"
              : "asc"
            : false}
          <Table.HeaderCell
            class="border-trade-desk-gray-dark relative flex border-r-2 border-b-[1px] border-solid px-1 {drag.columnClasses(
              name
            )}"
            span={1}
            style={widthStyle(name)}
          >
            <button
              type="button"
              class="flex min-w-0 flex-1 justify-between {reorderable
                ? 'cursor-move'
                : ''} {sortable ? '' : 'cursor-default'}"
              draggable={reorderable}
              onclick={col.getToggleSortingHandler()}
              ondragstart={(e) => drag.onDragStart(e, name)}
              ondragover={(e) => drag.onDragOver(e, name)}
              ondragleave={() => drag.onDragLeave(name)}
              ondrop={(e) => drag.onDrop(e, name)}
              ondragend={drag.cleanupDrag}
            >
              <span class="min-w-0 flex-1 truncate">
                <FlexRender
                  content={header.column.columnDef.header}
                  context={header.getContext()}
                />
              </span>
              <span class="shrink-0">
                {#if sortable}
                  {sortDir === "asc" ? "▲" : sortDir === "desc" ? "▼" : ""}
                {/if}
              </span>
            </button>
            {#if resizable && col.getCanResize()}
              <div
                class="group absolute inset-y-0 -right-2 z-10 w-4 cursor-col-resize"
                role="button"
                tabindex={0}
                aria-roledescription="column width editor"
                aria-label="Resize {name} column"
                data-testid="column-resizer-{name}"
                onmousedown={header.getResizeHandler()}
                ondblclick={(e) => autofitColumn(name, e)}
                onkeydown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    autofitColumn(name, e)
                  }
                }}
              >
                <div
                  class="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 group-hover:bg-blue-500/60"
                ></div>
              </div>
            {/if}
          </Table.HeaderCell>
        {/each}
      {/each}
    </div>
    <Table.Body class="min-h-0 flex-1">
      {#each rowsWithCells as { row, cells } (row.id)}
        {@const isGroup = isGroupRow(row.original)}
        {@const expandedMap = table.state.expanded as
          Record<string, boolean> | true | undefined}
        {@const isExpanded =
          expandedMap === true || expandedMap?.[row.id] === true}
        {#if isGroup}
          <div
            role="row"
            aria-rowindex={row.index + 1}
            class="body-row border-divider-horizontal-end flex w-fit min-w-full border-b leading-10 [&>*:first-child]:pl-2"
          >
            <Table.BodyCell
              class="border-trade-desk-gray-dark border-r-2 border-solid px-1"
              style="min-width: {totalColumnWidth}px; flex: 1 1 auto"
            >
              <button
                type="button"
                class="flex w-full items-center gap-3 text-left"
                onclick={() => row.toggleExpanded()}
              >
                <span
                  class="inline-block {isExpanded ? 'rotate-90' : ''}"
                  data-testid="arrow-button-{isExpanded ? 'open' : 'closed'}"
                  >&#x25b6</span
                >
                {(row.original as GroupRow).groupKey}
              </button>
            </Table.BodyCell>
          </div>
        {:else}
          <div
            role="row"
            aria-rowindex={row.index + 1}
            class="body-row border-divider-horizontal-end flex w-fit min-w-full border-b leading-10 focus:bg-blue-600 focus:text-white [&>*:first-child]:pl-2"
          >
            {#each cells as cell, col_index (cell.id)}
              {@const colStyle =
                columns.find((c) => c.name === cell.column.id)?.style ?? ""}
              <Table.BodyCell
                class="border-trade-desk-gray-dark border-r-2 border-solid px-1 {colStyle} {drag.columnClasses(
                  cell.column.id
                )}"
                span={1}
                style={col_index === 0 && grouped
                  ? `${widthStyle(cell.column.id)}; padding-left: 3rem`
                  : widthStyle(cell.column.id)}
              >
                <FlexRender
                  content={cell.column.columnDef.cell}
                  context={cell.getContext()}
                />
              </Table.BodyCell>
            {/each}
          </div>
        {/if}
      {/each}
    </Table.Body>
  </Table.Root>
</ScrollShadow>
